import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Switch,
  Dialog,
  MenuItem,
  Container,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import ConfirmationDialog from '../shared/ConfirmationDialog';

// ---------------------------------------------------------------------------
// Company Profile content — admin-managed items APPENDED to the hard-coded page
// (never replacing it). One collection, four sections:
//   partner  → the partners logo wall
//   diploma  → Kayfa for Learners programs
//   company  → Kayfa for Companies tracks
//   story    → success stories
// New items show up on the public page after the existing static ones.
// ---------------------------------------------------------------------------

type Section = 'partner' | 'diploma' | 'company' | 'story';

type Extra = {
  _id?: string;
  section: Section;
  title?: string;
  title_ar?: string;
  subtitle?: string;
  subtitle_ar?: string;
  description?: string;
  description_ar?: string;
  image?: string;
  link?: string;
  tools?: string[];
  points?: string[];
  points_ar?: string[];
  outcome?: string;
  outcome_ar?: string;
  icon?: string;
  category?: string;
  category_ar?: string;
  year?: string;
  order?: number;
  active?: boolean;
};

const SECTIONS: { value: Section; label: string; hint: string }[] = [
  { value: 'partner', label: 'Partner logo', hint: 'A logo on the “Our success partners” wall.' },
  { value: 'diploma', label: 'Learner diploma', hint: 'A card in “Kayfa for Learners” products.' },
  { value: 'company', label: 'Company track', hint: 'A card in “Kayfa for Companies” tracks.' },
  { value: 'story', label: 'Success story', hint: 'A card in the “Success stories” section.' },
];
const sectionLabel = (s?: string) => SECTIONS.find((x) => x.value === s)?.label || s || '';

// Company track icons — must match the FE company ICON map keys.
const COMPANY_ICONS = ['chart', 'users', 'data', 'spark', 'cap', 'globe', 'shield', 'target', 'rocket', 'star', 'ai', 'design'];

const EMPTY: Extra = { section: 'partner', active: true, order: 0, tools: [], points: [], points_ar: [] };

const toLines = (arr?: string[]) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const toCsv = (arr?: string[]) => (Array.isArray(arr) ? arr.join(', ') : '');
const fromCsv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

// ---------------------------------------------------------------------------

export default function CompanyProfileContent() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const canDelete = useIsFullAdmin();

  const [rows, setRows] = useState<Extra[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Section | 'all'>('all');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<Extra>(EMPTY);
  // Comma/newline buffers so the raw text isn't reparsed on every keystroke.
  const [toolsText, setToolsText] = useState('');
  const [pointsText, setPointsText] = useState('');
  const [pointsArText, setPointsArText] = useState('');
  const [deleteState, setDeleteState] = useState<{ open: boolean; id?: string }>({ open: false });
  const [deleting, setDeleting] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.COMPANY_EXTRAS.LIST);
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load items', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.section === filter)),
    [rows, filter],
  );

  const openNew = () => {
    const section: Section = filter === 'all' ? 'partner' : filter;
    setForm({ ...EMPTY, section, order: rows.filter((r) => r.section === section).length });
    setToolsText('');
    setPointsText('');
    setPointsArText('');
    setFormOpen(true);
  };
  const openEdit = (r: Extra) => {
    setForm({ ...EMPTY, ...r });
    setToolsText(toCsv(r.tools));
    setPointsText(toLines(r.points));
    setPointsArText(toLines(r.points_ar));
    setFormOpen(true);
  };
  const setField = (k: keyof Extra, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post(APIS.UPLOAD.MEDIA, fd);
      const url = res.data?.url ?? res.data?.data?.url;
      if (url) setField('image', url);
      else enqueueSnackbar('Upload returned no URL', { variant: 'error' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const sec = form.section;
    if (sec === 'partner' && !form.image) {
      enqueueSnackbar('A partner needs a logo image', { variant: 'warning' });
      return;
    }
    if (sec !== 'partner' && !form.title?.trim()) {
      enqueueSnackbar('Title is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    const payload: Extra = {
      section: sec,
      title: form.title?.trim() || '',
      title_ar: form.title_ar?.trim() || '',
      subtitle: form.subtitle?.trim() || '',
      subtitle_ar: form.subtitle_ar?.trim() || '',
      description: form.description?.trim() || '',
      description_ar: form.description_ar?.trim() || '',
      image: form.image?.trim() || '',
      link: form.link?.trim() || '',
      tools: fromCsv(toolsText),
      points: fromLines(pointsText),
      points_ar: fromLines(pointsArText),
      outcome: form.outcome?.trim() || '',
      outcome_ar: form.outcome_ar?.trim() || '',
      icon: form.icon?.trim() || '',
      category: form.category?.trim() || '',
      category_ar: form.category_ar?.trim() || '',
      year: form.year?.trim() || '',
      order: Number(form.order) || 0,
      active: form.active !== false,
    };
    try {
      if (form._id) await axiosInstance.patch(APIS.COMPANY_EXTRAS.PATCH(form._id), payload);
      else await axiosInstance.post(APIS.COMPANY_EXTRAS.ADD, payload);
      enqueueSnackbar(form._id ? 'Item updated' : 'Item created');
      setFormOpen(false);
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.messages?.[0] || e?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(APIS.COMPANY_EXTRAS.DELETE(deleteState.id));
      enqueueSnackbar('Item deleted');
      setDeleteState({ open: false });
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const sec = form.section;
  const hasImage = sec === 'partner' || sec === 'story';
  const hasKind = sec === 'diploma' || sec === 'company' || sec === 'story';
  const hasDesc = sec !== 'partner';
  const hasTools = sec === 'diploma' || sec === 'company';
  const hasPoints = sec === 'company';
  const hasOutcome = sec === 'company';
  const hasIcon = sec === 'company';
  const hasStoryMeta = sec === 'story';
  const hasLink = sec === 'partner';
  const kindLabel =
    sec === 'diploma' ? 'Label (e.g. "Diploma · 5 months")'
    : sec === 'company' ? 'Format (e.g. "On-site / Remote")'
    : 'Role / subtitle';

  return (
    <Page title="Company Profile">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Company Profile"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Company Profile' }]}
          action={
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openNew}>
              New item
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Items you add here are <b>appended</b> to the existing Company Profile content — nothing
            built into the page is removed. Choose a section, fill the fields, set Order (lower shows
            first) and keep Active on to publish.
          </Typography>
        </Card>

        <Card sx={{ p: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Show section"
            value={filter}
            onChange={(e) => setFilter(e.target.value as Section | 'all')}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All sections</MenuItem>
            {SECTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
        </Card>

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }} />
            )}
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Preview</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>Order</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No items yet — click “New item” to add one.
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell>
                      {r.image ? (
                        <Avatar
                          src={r.image}
                          variant="rounded"
                          sx={{ width: 44, height: 44, bgcolor: 'background.neutral', '& img': { objectFit: 'contain' } }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: 'background.neutral', color: 'text.disabled' }}>
                          <Iconify icon="eva:file-text-outline" />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Label color="info">{sectionLabel(r.section)}</Label>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" noWrap>{r.title || (r.section === 'partner' ? '(logo)' : '—')}</Typography>
                      {r.subtitle ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{r.subtitle}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">{r.order ?? 0}</TableCell>
                    <TableCell align="center">
                      <Label color={r.active !== false ? 'success' : 'default'}>
                        {r.active !== false ? 'Active' : 'Hidden'}
                      </Label>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEdit(r)}>
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      {canDelete && (
                        <IconButton sx={{ color: 'error.main' }} onClick={() => setDeleteState({ open: true, id: r._id })}>
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Edit item' : 'New item'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              select
              label="Section"
              value={form.section}
              onChange={(e) => setField('section', e.target.value)}
              fullWidth
              disabled={!!form._id}
              helperText={SECTIONS.find((s) => s.value === form.section)?.hint}
            >
              {SECTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>

            {hasImage && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={form.image}
                  variant="rounded"
                  sx={{ width: 64, height: 64, bgcolor: 'background.neutral', '& img': { objectFit: 'contain' } }}
                >
                  <Iconify icon="eva:image-outline" />
                </Avatar>
                <Button variant="outlined" component="label" disabled={uploading} startIcon={<Iconify icon="eva:cloud-upload-outline" />}>
                  {uploading ? 'Uploading…' : sec === 'partner' ? 'Upload logo' : 'Upload photo'}
                  <input hidden type="file" accept="image/*" onChange={(e) => handleUpload(e.target.files?.[0])} />
                </Button>
              </Stack>
            )}

            <TextField
              label={sec === 'partner' ? 'Name (for alt text, optional)' : 'Title (English)'}
              value={form.title || ''}
              onChange={(e) => setField('title', e.target.value)}
              fullWidth
              required={sec !== 'partner'}
            />
            {sec !== 'partner' && (
              <TextField label="Title (Arabic)" value={form.title_ar || ''} onChange={(e) => setField('title_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
            )}

            {hasKind && (
              <>
                <TextField label={kindLabel} value={form.subtitle || ''} onChange={(e) => setField('subtitle', e.target.value)} fullWidth />
                <TextField label="Arabic label" value={form.subtitle_ar || ''} onChange={(e) => setField('subtitle_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasDesc && (
              <>
                <TextField label="Description (English)" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} fullWidth multiline minRows={2} />
                <TextField label="Description (Arabic)" value={form.description_ar || ''} onChange={(e) => setField('description_ar', e.target.value)} fullWidth multiline minRows={2} inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasIcon && (
              <TextField select label="Icon" value={form.icon || ''} onChange={(e) => setField('icon', e.target.value)} fullWidth>
                <MenuItem value=""><em>Default</em></MenuItem>
                {COMPANY_ICONS.map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </TextField>
            )}

            {hasTools && (
              <TextField
                label="Tags / tools (comma-separated)"
                value={toolsText}
                onChange={(e) => setToolsText(e.target.value)}
                fullWidth
                placeholder="Python, SQL, Power BI"
              />
            )}

            {hasPoints && (
              <>
                <TextField label="Modules (one per line)" value={pointsText} onChange={(e) => setPointsText(e.target.value)} fullWidth multiline minRows={3} />
                <TextField label="Modules — Arabic (one per line)" value={pointsArText} onChange={(e) => setPointsArText(e.target.value)} fullWidth multiline minRows={3} inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasOutcome && (
              <>
                <TextField label="Outcome (English)" value={form.outcome || ''} onChange={(e) => setField('outcome', e.target.value)} fullWidth />
                <TextField label="Outcome (Arabic)" value={form.outcome_ar || ''} onChange={(e) => setField('outcome_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasStoryMeta && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                <TextField label="Category (e.g. Event)" value={form.category || ''} onChange={(e) => setField('category', e.target.value)} fullWidth />
                <TextField label="Category (Arabic)" value={form.category_ar || ''} onChange={(e) => setField('category_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
                <TextField label="Year" value={form.year || ''} onChange={(e) => setField('year', e.target.value)} sx={{ maxWidth: 120 }} />
              </Stack>
            )}

            {hasLink && (
              <TextField label="Website / verify link (optional)" value={form.link || ''} onChange={(e) => setField('link', e.target.value)} fullWidth placeholder="https://…" />
            )}

            <Stack direction="row" spacing={2.5} alignItems="center">
              <TextField
                label="Order"
                type="number"
                value={form.order ?? 0}
                onChange={(e) => setField('order', e.target.value)}
                sx={{ maxWidth: 140 }}
                helperText="Lower shows first"
              />
              <FormControlLabel
                control={<Switch checked={form.active !== false} onChange={(e) => setField('active', e.target.checked)} />}
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDelete}
        headText="Delete item"
        text="Are you sure you want to delete this Company Profile item?"
        loading={deleting}
      />
    </Page>
  );
}
